/**
 * ALM Target Picker - selects the tracker an output node writes into by pasting its tracker
 * URL or ID (resolved via POST /workflows/resolve-alm-url). The internal scope/project ids it
 * resolves to are stored on the node but never entered by hand.
 */

import React, { useState } from 'react';
import { pipelineApi } from '../api/client';

export interface AlmTarget {
  scopeId: string;
  projectId: string;
  typeId: string;
}

interface AlmTargetPickerProps {
  value: AlmTarget;
  onChange: (value: AlmTarget) => void;
}

export const AlmTargetPicker: React.FC<AlmTargetPickerProps> = ({ value, onChange }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const handleUrlBlur = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setError(null);
    setResolving(true);
    try {
      const result = await pipelineApi.resolveAlmUrl(trimmed, 'tracker_id');
      if (result.not_found || result.scope === null || result.scope.type_id !== 'ScopeFromSelector') {
        setError('Could not resolve this tracker.');
        return;
      }
      onChange({
        scopeId: result.scope.scope_id,
        projectId: result.scope.scope_project_id || '',
        typeId: result.scope.scope_type_id || '',
      });
      setUrl('');
    } catch (err) {
      const detail = (err as any)?.response?.data?.detail;
      setError(detail || (err instanceof Error ? err.message : 'Failed to resolve URL.'));
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="condition-row">
      <label>Tracker URL or ID</label>
      <input
        type="text"
        className="condition-input"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          setError(null);
        }}
        onBlur={handleUrlBlur}
        placeholder="e.g. https://codebeamer.example.com/cb/tracker/12396"
        disabled={resolving}
      />
      {resolving && <p className="field-hint">Resolving...</p>}
      {error && <p className="field-error">{error}</p>}
      {!resolving && !error && (
        <p className="field-hint">
          {value.typeId ? `Target: tracker ${value.typeId} (project ${value.projectId})` : 'No target tracker selected.'}
        </p>
      )}
    </div>
  );
};
